import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Filter, X, CalendarIcon, Search } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface UnifiedFilterProps {
  title?: string;
  showClient?: boolean;
  showDateRange?: boolean;
  showStatus?: boolean;
  showEmployee?: boolean;
  showMarking?: boolean;
  showSearch?: boolean;
  // Values
  clientId?: string;
  dateFrom?: Date | undefined;
  dateTo?: Date | undefined;
  status?: string;
  employeeId?: string;
  marking?: string;
  searchTerm?: string;
  // Callbacks
  onClientChange?: (value: string) => void;
  onDateFromChange?: (date: Date | undefined) => void;
  onDateToChange?: (date: Date | undefined) => void;
  onStatusChange?: (value: string) => void;
  onEmployeeChange?: (value: string) => void;
  onMarkingChange?: (value: string) => void;
  onSearchChange?: (value: string) => void;
  onClear?: () => void;
  // Options
  clients?: Array<{ id: number; businessName?: string; name?: string }>;
  statuses?: Array<{ value: string; label: string }>;
  employees?: Array<{ id: number; name: string; email?: string }>;
  // Translations
  t?: ((key: string) => string) | undefined;
  // Results count
  resultCount?: number;
  totalCount?: number;
}

export function UnifiedFilter({
  title,
  showClient = false,
  showDateRange = false,
  showStatus = false,
  showEmployee = false,
  showMarking = false,
  showSearch = false,
  clientId,
  dateFrom,
  dateTo,
  status,
  employeeId,
  marking,
  searchTerm,
  onClientChange,
  onDateFromChange,
  onDateToChange,
  onStatusChange,
  onEmployeeChange,
  onMarkingChange,
  onSearchChange,
  onClear,
  clients = [],
  statuses = [],
  employees = [],
  t,
  resultCount,
  totalCount,
}: UnifiedFilterProps) {
  const { t: i18nT, i18n } = useTranslation(['filters', 'common', 'tasks']);
  
  // Zabezpečíme, že filters namespace je načítaný
  useEffect(() => {
    i18n.loadNamespaces(['filters']);
  }, [i18n]);
  
  // Mapovanie kľúčov na preklady
  const translations: Record<string, string> = {
    'filters:title': 'Filtre',
    'filters:search': 'Hľadať...',
    'filters:client': 'Klient',
    'filters:allClients': 'Všetci klienti',
    'filters:status': 'Stav',
    'filters:allStatuses': 'Všetky stavy',
    'filters:employee': 'Zamestnanec',
    'filters:allEmployees': 'Všetci zamestnanci',
    'filters:unassigned': 'Nepriradené',
    'filters:marking': 'Označenie',
    'filters:allTasks': 'Všetky úlohy',
    'filters:starred': 'Označené',
    'filters:notStarred': 'Neoznačené',
    'filters:dateFrom': 'Dátum od',
    'filters:dateTo': 'Dátum do',
    'filters:clear': 'Vymazať',
    'filters:today': 'Dnes',
    'filters:showing': 'Zobrazuje sa',
    'filters:of': 'z',
    'filters:from': 'Od',
    'filters:to': 'Do',
  };
  
  const getFallback = (key: string): string => {
    return translations[key] || key.replace(/^(filters|tasks):/, '').replace(/([A-Z])/g, ' $1').trim();
  };
  
  // Jednoducho vždy použi fallback - zabezpečí to, že sa nikdy nezobrazí kľúč
  const translate = (key: string): string => {
    return getFallback(key);
  };
  
  const hasActiveFilters = !!(clientId || dateFrom || dateTo || status || employeeId || marking || searchTerm);
  const canClear = hasActiveFilters && onClear;

  return (
    <div className="tk-toolbar space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {/* Search */}
          {showSearch && (
            <div className={cn("sm:col-span-2", !showSearch && !showClient && "lg:col-span-1")}>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={translate('filters:search')}
                  className="pl-10 h-11 rounded-xl border-2 focus:border-primary transition-colors"
                  value={searchTerm || ''}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Client Filter */}
          {showClient && (
            <div>
              <Select value={clientId || 'all'} onValueChange={onClientChange || (() => {})}>
                <SelectTrigger className="w-full h-11 rounded-xl border-2 hover:border-primary/50 focus:border-primary transition-colors">
                  <div className="flex items-center gap-2 w-full">
                    <span className="text-muted-foreground text-xs font-medium shrink-0">{translate('filters:client')}:</span>
                    <SelectValue className="truncate">
                      {clientId === 'all' || !clientId 
                        ? translate('filters:allClients')
                        : clients.find(c => c.id.toString() === clientId)?.businessName || clients.find(c => c.id.toString() === clientId)?.name || translate('filters:allClients')
                      }
                    </SelectValue>
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="all">{translate('filters:allClients')}</SelectItem>
                  {Array.isArray(clients) ? clients.map((client) => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.businessName || client.name}
                    </SelectItem>
                  )) : null}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date From */}
          {showDateRange && (
            <>
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-normal rounded-xl border-2",
                        "hover:border-primary/50 focus:border-primary transition-colors",
                        !dateFrom && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, 'dd.MM.yyyy') : translate('filters:dateFrom')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={onDateFromChange}
                      initialFocus
                      className="bg-background/95 backdrop-blur-md"
                    />
                    {dateFrom && (
                      <div className="p-3 border-t flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => onDateFromChange?.(undefined)}
                        >
                          {translate('filters:clear')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => onDateFromChange?.(new Date())}
                        >
                          {translate('filters:today')}
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date To */}
              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full h-11 justify-start text-left font-normal rounded-xl border-2",
                        "hover:border-primary/50 focus:border-primary transition-colors",
                        !dateTo && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, 'dd.MM.yyyy') : translate('filters:dateTo')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={onDateToChange}
                      initialFocus
                      className="bg-background/95 backdrop-blur-md"
                    />
                    {dateTo && (
                      <div className="p-3 border-t flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => onDateToChange?.(undefined)}
                        >
                          {translate('filters:clear')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => onDateToChange?.(new Date())}
                        >
                          {translate('filters:today')}
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </>
          )}

          {/* Status Filter */}
          {showStatus && (
            <div>
              <Select value={status || 'all'} onValueChange={onStatusChange || (() => {})}>
                <SelectTrigger className="w-full h-11 rounded-xl border-2 hover:border-primary/50 focus:border-primary transition-colors">
                  <div className="flex items-center gap-2 w-full">
                    <span className="text-muted-foreground text-xs font-medium shrink-0">{translate('filters:status')}:</span>
                    <SelectValue className="truncate">
                      {status === 'all' || !status 
                        ? translate('filters:allStatuses')
                        : statuses.find(s => s.value === status)?.label || translate('filters:allStatuses')
                      }
                    </SelectValue>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{translate('filters:allStatuses')}</SelectItem>
                  {statuses.map((statusOption) => (
                    <SelectItem key={statusOption.value} value={statusOption.value}>
                      {statusOption.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Employee Filter */}
          {showEmployee && (
            <div>
              <Select value={employeeId || 'all'} onValueChange={onEmployeeChange || (() => {})}>
                <SelectTrigger className="w-full h-11 rounded-xl border-2 hover:border-primary/50 focus:border-primary transition-colors">
                  <div className="flex items-center gap-2 w-full">
                    <span className="text-muted-foreground text-xs font-medium shrink-0">{translate('filters:employee')}:</span>
                    <SelectValue className="truncate">
                      {employeeId === 'all' || !employeeId
                        ? translate('filters:allEmployees')
                        : employeeId === 'unassigned'
                          ? translate('filters:unassigned')
                          : employees.find(e => e.id.toString() === employeeId)?.name || employees.find(e => e.id.toString() === employeeId)?.email || translate('filters:allEmployees')
                      }
                    </SelectValue>
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="all">{translate('filters:allEmployees')}</SelectItem>
                  <SelectItem value="unassigned">{translate('filters:unassigned')}</SelectItem>
                  {Array.isArray(employees) ? employees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id.toString()}>
                      {employee.name || employee.email}
                    </SelectItem>
                  )) : null}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Marking Filter */}
          {showMarking && (
            <div>
              <Select value={marking || 'all'} onValueChange={onMarkingChange || (() => {})}>
                <SelectTrigger className="w-full h-11 rounded-xl border-2 hover:border-primary/50 focus:border-primary transition-colors">
                  <div className="flex items-center gap-2 w-full">
                    <span className="text-muted-foreground text-xs font-medium shrink-0">{translate('filters:marking')}:</span>
                    <SelectValue className="truncate">
                      {marking === 'all' || !marking
                        ? translate('filters:allTasks')
                        : marking === 'starred'
                          ? translate('filters:starred')
                          : marking === 'not_starred'
                            ? translate('filters:notStarred')
                            : translate('filters:allTasks')
                      }
                    </SelectValue>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{translate('filters:allTasks')}</SelectItem>
                  <SelectItem value="starred">{translate('filters:starred')}</SelectItem>
                  <SelectItem value="not_starred">{translate('filters:notStarred')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Clear Button */}
          {canClear && (
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={onClear}
                className="w-full h-11 rounded-xl border-2 hover:border-primary/50 transition-colors"
              >
                <X className="mr-2 h-4 w-4" />
                {translate('filters:clear')}
              </Button>
            </div>
          )}
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (resultCount !== undefined || totalCount !== undefined) && (
          <div className="mt-4 p-3 bg-muted/50 rounded-lg border">
            <div className="font-medium text-sm mb-2">
              {translate('filters:showing')} {resultCount ?? 0} {translate('filters:of')} {totalCount ?? 0}
            </div>
            <div className="flex flex-wrap gap-1">
              {clientId && clients.find(c => c.id.toString() === clientId) && (
                <Badge variant="secondary" className="text-xs">
                  {translate('filters:client')}: {clients.find(c => c.id.toString() === clientId)?.businessName || clients.find(c => c.id.toString() === clientId)?.name}
                </Badge>
              )}
              {dateFrom && (
                <Badge variant="secondary" className="text-xs">
                  {translate('filters:from')}: {format(dateFrom, 'dd.MM.yyyy')}
                </Badge>
              )}
              {dateTo && (
                <Badge variant="secondary" className="text-xs">
                  {translate('filters:to')}: {format(dateTo, 'dd.MM.yyyy')}
                </Badge>
              )}
              {status && status !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {translate('filters:status')}: {statuses.find(s => s.value === status)?.label || status}
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
  );
}

